import { useState, useRef, useEffect } from "react";
import toast from "react-hot-toast";
import { Spinner } from "@chakra-ui/react";
import { SendIcon } from "../../Assets/SvgIcons";
import { useParams } from "react-router-dom";
import CustomMention from "../CustomMentions/CustomMentions";
import { SuggestionDataItem } from "../../../types";

import {
  CommentSpacer,
  CommentsAndInputWrapper,
  CommentsWrapper,
  CommentInputContainer,
  CommentInputWrapper,
  CommentSendButtonWrapper,
  mentionStyle,
} from "./styles";

import { Mention, MentionsInput } from "react-mentions";

import {
  useAddInteractionsToPostMutation,
  useGetProfileQuery,
  useTagUserMutation,
} from "../../../redux/reducers/apiSlice";
import { getUsersFromTags } from "../../utils/helper";
import { Interaction } from "../../../redux/reducers/types";
import { Comment } from "../Comment/Comment";
import { useMentions } from "../../hooks/useMentions";
import * as Sentry from "@sentry/react";
interface Props {
  comments: Interaction[];
  visible: boolean;
  postId: string;
  fullHeight?: boolean;
}

export const Comments: React.FC<Props> = ({
  comments,
  visible,
  postId,
  fullHeight,
}) => {
  const { interactionId } = useParams();
  const [comment, setComment] = useState("");
  const [showInput, setShowInput] = useState(true);
  const commentsRef = useRef<HTMLDivElement>(null);
  const commentIdsRef = useRef<HTMLDivElement | {}>({});
  const [addComment, { isLoading: isAddCommentLoading }] =
    useAddInteractionsToPostMutation();
  const [tagUser, { isLoading: loadingTagUser }] = useTagUserMutation();
  const { data: currentUserProfile } = useGetProfileQuery();

  const { mentionsData } = useMentions();

  // When comments section opens, hide input if there are existing comments
  useEffect(() => {
    if (visible) {
      if (comments.length > 0) {
        setShowInput(false);
      } else {
        setShowInput(true);
      }
    }
  }, [visible]);

  const submitComment = async () => {
    try {
      const commentData = await addComment({
        type: "COMMENT",
        id: postId,
        data: {
          message: comment,
        },
      }).unwrap();

      await tagUser({
        targetUserIds: getUsersFromTags(comment).map((user) => user.id),
        postId: postId,
        interactionId: commentData.id,
      });

      setComment("");
      setShowInput(false); // Hide input after posting
      if (commentsRef?.current) {
        commentsRef.current.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    } catch (e) {
      Sentry.captureException(e);
      toast.error("Something went wrong while trying to add your comment");
    }
  };

  useEffect(() => {
    if (interactionId) {
      scrollToSpecificComment(interactionId);
    }
  }, [interactionId]);

  const scrollToSpecificComment = (commentId: string) => {
    if (commentIdsRef?.current && commentIdsRef?.current[commentId]) {
      commentIdsRef.current[commentId].scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  return (
    <CommentsAndInputWrapper show={visible}>
      {showInput && (
        <CommentInputContainer>
          <CommentInputWrapper>
            <MentionsInput
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              maxLength={200}
              placeholder="Enter your comment here..."
              singleLine
              style={mentionStyle}
              data-cy="cypress-comment"
            >
              <Mention
                trigger="@"
                data={mentionsData}
                className="mention"
                renderSuggestion={(suggestionDataItem: SuggestionDataItem) => (
                  <CustomMention suggestionDataItem={suggestionDataItem} />
                )}
              />
            </MentionsInput>
            <CommentSendButtonWrapper>
              <button
                disabled={isAddCommentLoading || !comment.trim()} 
                onClick={submitComment} 
                id="cypress-submit-comment-button"
                type="button"
                style={{
                  background: comment.trim() ? "#007bff" : "transparent",
                  border: "none",
                  borderRadius: "50%",
                  cursor: isAddCommentLoading || !comment.trim() ? "not-allowed" : "pointer",
                  opacity: isAddCommentLoading || !comment.trim() ? 0.4 : 1,
                  padding: "0",
                  margin: "0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s ease",
                  width: "36px",
                  height: "36px",
                  minWidth: "36px",
                  minHeight: "36px",
                  boxSizing: "border-box",
                }}
                onMouseEnter={(e) => {
                  if (comment.trim() && !isAddCommentLoading) {
                    e.currentTarget.style.background = "#0056b3";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = comment.trim() ? "#007bff" : "transparent";
                }}
              >
                {isAddCommentLoading ? (
                  <Spinner 
                    size="sm" 
                    color="white"
                  />
                ) : (
                  <SendIcon 
                    style={{ 
                      width: "18px", 
                      height: "18px",
                      color: comment.trim() ? "white" : "#999",
                      margin: "0",
                      padding: "0",
                      display: "block",
                      flexShrink: "0",
                    }} 
                  />
                )}
              </button>
            </CommentSendButtonWrapper>
          </CommentInputWrapper>
        </CommentInputContainer>
      )}
      {comments.length > 0 && (
        <CommentsWrapper
          fullHeight={fullHeight}
          hasOneComment={comments.length <= 1}
          showInput={showInput}
          ref={commentsRef}
        >
          {!showInput && (
            <button
              onClick={() => setShowInput(true)}
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: "8px 0",
                marginBottom: "8px",
                color: "#007bff",
                fontSize: "14px",
                fontWeight: "500",
                textAlign: "left",
                width: "100%",
              }}
            >
              Add a comment...
            </button>
          )}
          {comments.map((comment) => (
            <CommentSpacer key={comment.id}>
              <Comment
                ref={(el) => {
                  if (commentIdsRef.current) {
                    commentIdsRef.current[comment.id] = el;
                  }
                }}
                fromNotification={comment.id === interactionId}
                comment={comment}
                postId={postId}
                currentUserId={currentUserProfile?.id}
              />
            </CommentSpacer>
          ))}
        </CommentsWrapper>
      )}
      {comments.length === 0 && showInput && (
        <div style={{ marginTop: "16px", fontSize: "14px", color: "#999", textAlign: "center" }}>
          No comments yet. Be the first to comment!
        </div>
      )}
    </CommentsAndInputWrapper>
  );
};
